import {
    ComponentPropsWithoutRef,
    ReactNode,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrentLetter } from './useCurrentLetter';
import { generateEngageLetterZip } from 'api/engage';
import { MessageStatus } from 'constants/messages';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import prettyBytes from 'pretty-bytes';
import { EngageDownload } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ILetterDownloadContext {
    download: EngageDownload | null;
}

const LetterDownloadContext = createContext<ILetterDownloadContext>({
    download: null,
});

interface LetterDownloadProps {
    download: EngageDownload | null;
    children: ReactNode;
}

const LetterDownloadProvider = (props: LetterDownloadProps) => {
    const { download, children } = props;
    const value = useMemo(() => ({ download }), [download]);

    return (
        <LetterDownloadContext.Provider value={value}>{children}</LetterDownloadContext.Provider>
    );
};

interface LetterDownloadContainerProps extends ComponentPropsWithoutRef<'div'> {}

const LetterDownloadContainer = (props: LetterDownloadContainerProps) => {
    const { className = '', ...rest } = props;

    return (
        <div className={`flex flex-wrap items-center justify-between p-4 ${className}`} {...rest} />
    );
};

interface LetterDownloadDescriptionContainerProps extends ComponentPropsWithoutRef<'div'> {}

const LetterDownloadDescriptionContainer = (props: LetterDownloadDescriptionContainerProps) => {
    const { className = '', ...rest } = props;
    const { download } = useContext(LetterDownloadContext);
    const isGenerating = !download;
    const generatingClassName = isGenerating ? 'opacity-60' : '';

    return <div className={`${generatingClassName} ${className}`} {...rest} />;
};

interface LetterDownloadTitleProps extends ComponentPropsWithoutRef<'div'> {
    name?: string;
}

const LetterDownloadTitle = (props: LetterDownloadTitleProps) => {
    const { name, className = '', children, ...rest } = props;
    const { download } = useContext(LetterDownloadContext);

    return (
        <div className={`flex items-center gap-1 ${className}`} {...rest}>
            <p className={'text-sm font-medium text-gray-800'}>{children ?? download?.name}</p>
            <p className={'text-sm text-gray-400'}>{prettyBytes(download?.size ?? 0)}</p>
        </div>
    );
};

interface LetterDownloadDescriptionProps extends ComponentPropsWithoutRef<'p'> {}

const LetterDownloadDescription = (props: LetterDownloadDescriptionProps) => {
    const { className = '', ...rest } = props;

    return <p className={`text-sm text-gray-600 ${className}`} {...rest} />;
};

interface LetterDownloadStatusProps {
    type: string;
}

const LetterDownloadStatus = (props: LetterDownloadStatusProps) => {
    const { type } = props;
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { download } = useContext(LetterDownloadContext);

    const [zipFileRequested, setZipFileRequested] = useState(false);

    const { mutate: requestZipDownload, isPending: isZipRequestLoading } = useMutation({
        mutationFn: () => generateEngageLetterZip(accountId, letter.id),
        onSuccess: () => {
            setZipFileRequested(true);

            success(`Success! We'll email you when the download is ready. This may take a while.`);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Sorry, something went wrong. Please try again.');
        },
    });

    if (!download) {
        if (
            zipFileRequested ||
            ![MessageStatus.SENDING, MessageStatus.SENT].some(s => s === letter.status) ||
            type !== 'zip'
        ) {
            return (
                <div className={'flex items-center gap-2'}>
                    <FontAwesomeIcon className={'animate-spin text-blue-500'} icon={faSpinner} />
                    <p className={'font-medium text-gray-700'}>
                        Generating {type.toUpperCase()} file...
                    </p>
                </div>
            );
        }

        return (
            <Button
                variant="transparent"
                className="p-0"
                disabled={isZipRequestLoading}
                onClick={() => requestZipDownload()}
            >
                Request
            </Button>
        );
    }

    return (
        <a className={'text-blue-500'} download={download.name} href={download.url}>
            Download
        </a>
    );
};

const Root = LetterDownloadProvider;
const Container = LetterDownloadContainer;
const DescriptionContainer = LetterDownloadDescriptionContainer;
const Title = LetterDownloadTitle;
const Description = LetterDownloadDescription;
const Status = LetterDownloadStatus;

export {
    Root,
    Container,
    DescriptionContainer,
    Title,
    Description,
    Status,
    //
    LetterDownloadProvider,
    LetterDownloadContainer,
    LetterDownloadDescriptionContainer,
    LetterDownloadDescription,
    LetterDownloadTitle,
    LetterDownloadStatus,
};

export type {
    ILetterDownloadContext,
    LetterDownloadProps,
    LetterDownloadContainerProps,
    LetterDownloadDescriptionContainerProps,
    LetterDownloadTitleProps,
    LetterDownloadDescriptionProps,
    LetterDownloadStatusProps,
};
